
import React, { useRef, useState } from "react";
import { Table, Input, Button, Space, InputRef } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import type { ColumnType, ColumnsType } from "antd/es/table";
import type { FilterConfirmProps } from "antd/es/table/interface";
import useSWR from "swr";
import "./location_code_rack_picking_list.css";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

export interface RackLocationRecord {
    id: number;
    code_rack: string;
    name_rack: string;
    warehouse_code?: string;
}

interface LocationCodeRackPickingListProps {
    warehouseGroup?: string;
    itemCode?: string;
    onSelectLocation?: (record: RackLocationRecord) => void;
}

type DataIndex = keyof RackLocationRecord;

const fetcher = (url: string) => fetch(url).then((res) => res.json());

const LocationCodeRackPickingList: React.FC<LocationCodeRackPickingListProps> = ({ warehouseGroup, itemCode, onSelectLocation }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState<DataIndex | ''>('');
    const searchInput = useRef<InputRef>(null);

    const url = itemCode
        ? `${backendUrl}/api/spareparts-rack-locations?item_code=${encodeURIComponent(itemCode)}${warehouseGroup ? `&warehouse_code=${encodeURIComponent(warehouseGroup)}` : ''}`
        : warehouseGroup
            ? `${backendUrl}/api/spareparts-rack-locations?warehouse_code=${encodeURIComponent(warehouseGroup)}`
            : `${backendUrl}/api/spareparts-rack-locations`;
    const { data: rackData, isLoading } = useSWR<RackLocationRecord[]>(url, fetcher);

    const handleSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<RackLocationRecord> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.replace('_', ' ')}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters && handleReset(clearFilters)} size="small" style={{ width: 90 }}>Reset</Button>
                    <Button type="link" size="small" onClick={() => close()}>Close</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value, record) => !!record[dataIndex]?.toString().toLowerCase().includes((value as string).toLowerCase()),
        filterDropdownProps: { onOpenChange: (visible) => { if (visible) setTimeout(() => searchInput.current?.select(), 100); } },
        render: (text) => searchedColumn === dataIndex ? (
            <Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} searchWords={[searchText]} autoEscape textToHighlight={text ? text.toString() : ''} />
        ) : (text),
    });

    const columns: ColumnsType<RackLocationRecord> = [
        {
            title: 'Location Code',
            dataIndex: 'code_rack',
            key: 'code_rack',
            sorter: (a, b) => (a.code_rack || '').localeCompare(b.code_rack || ''),
            ...getColumnSearchProps('code_rack'),
        },
        {
            title: 'Location Name',
            dataIndex: 'name_rack',
            key: 'name_rack',
            sorter: (a, b) => (a.name_rack || '').localeCompare(b.name_rack || ''),
            ...getColumnSearchProps('name_rack'),
        },
    ];

    return (
        <div id="iniz" style={transformStyle}>
            <div className="gjs-grid-row" id="i5pwx">
                <div className="gjs-grid-column" id="imq76">
                    <Table<RackLocationRecord>
                        rowKey="id"
                        columns={columns}
                        dataSource={rackData || []}
                        loading={isLoading}
                        bordered
                        size="small"
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                onSelectLocation?.(record);
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default LocationCodeRackPickingList;